/* ==========================================================================
   Brota — comportamiento de interfaz
   Menú móvil, cajón de carrito y agregado sin recargar la página.
   El selector "¿Qué necesitás hoy?" vive en su propia sección.
   ========================================================================== */

(function () {
  'use strict';

  const SELECTOR_FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* --- Capa reutilizable para menú y carrito ------------------------------ */
  class CapaModal {
    constructor(elemento) {
      this.elemento = elemento;
      this.abierta = false;
      this.disparador = null;
      this.alTeclado = this.alTeclado.bind(this);
    }

    abrir(disparador) {
      if (!this.elemento || this.abierta) return;

      this.disparador = disparador || null;
      this.elemento.hidden = false;
      this.abierta = true;
      document.body.style.overflow = 'hidden';

      if (this.disparador) this.disparador.setAttribute('aria-expanded', 'true');

      const primero = this.elemento.querySelector(SELECTOR_FOCUSABLE);
      if (primero) primero.focus();

      /* Atrapar el Tab no alcanza: con los gestos de VoiceOver o TalkBack se
         puede recorrer el resto del documento igual. `inert` lo saca del árbol
         de accesibilidad y del foco de una.

         Se calcula relativo al panel y no con una lista fija de selectores,
         porque CapaModal la comparten el menú y el cajón de carrito: un
         selector fijo terminaría inertizando al propio carrito al abrirlo. */
      this.inertados = Array.from(document.body.children).filter(
        (nodo) => !nodo.contains(this.elemento)
      );
      this.inertados.forEach((nodo) => nodo.setAttribute('inert', ''));

      document.addEventListener('keydown', this.alTeclado);
    }

    cerrar() {
      if (!this.elemento || !this.abierta) return;

      this.elemento.hidden = true;
      this.abierta = false;
      document.body.style.overflow = '';

      if (this.inertados) {
        this.inertados.forEach((nodo) => nodo.removeAttribute('inert'));
        this.inertados = null;
      }

      if (this.disparador) {
        this.disparador.setAttribute('aria-expanded', 'false');
        this.disparador.focus();
      }

      document.removeEventListener('keydown', this.alTeclado);
    }

    /* Escape cierra; Tab queda encerrado dentro de la capa. */
    alTeclado(evento) {
      if (evento.key === 'Escape') {
        evento.preventDefault();
        this.cerrar();
        return;
      }

      if (evento.key !== 'Tab') return;

      const focusables = Array.from(this.elemento.querySelectorAll(SELECTOR_FOCUSABLE)).filter(
        (nodo) => nodo.offsetParent !== null
      );
      if (focusables.length === 0) return;

      const primero = focusables[0];
      const ultimo = focusables[focusables.length - 1];

      if (evento.shiftKey && document.activeElement === primero) {
        evento.preventDefault();
        ultimo.focus();
      } else if (!evento.shiftKey && document.activeElement === ultimo) {
        evento.preventDefault();
        primero.focus();
      }
    }
  }

  /* --- Menú móvil -------------------------------------------------------- */
  function iniciarMenu() {
    const panel = document.querySelector('[data-menu]');
    if (!panel) return;

    const capa = new CapaModal(panel);

    document.querySelectorAll('[data-menu-abrir]').forEach((boton) => {
      boton.addEventListener('click', () => capa.abrir(boton));
    });

    document.querySelectorAll('[data-menu-cerrar]').forEach((boton) => {
      boton.addEventListener('click', () => capa.cerrar());
    });

    /* Tocar cualquier enlace del panel tiene que cerrarlo. Sin esto, el CTA
       (que apunta a un ancla de la misma página) no hacía NADA visible: la
       guarda de misma-página de la cortina lo deja navegar, pero abrir() dejó
       body con overflow: hidden, así que el documento no podía scrollear hasta
       el ancla. Lo único que cambiaba era el hash. */
    panel.addEventListener('click', (evento) => {
      if (evento.target.closest('a[href]')) capa.cerrar();
    });

    /* Al volver con el botón atrás la página puede restaurarse desde el
       bfcache con el panel abierto y el scroll bloqueado. */
    window.addEventListener('pageshow', () => capa.cerrar());
  }

  /* --- Carrito ----------------------------------------------------------- */
  const carrito = {
    capa: null,

    iniciar() {
      const panel = document.querySelector('[data-carrito-cajon]');
      if (!panel) return;

      this.capa = new CapaModal(panel);

      document.addEventListener('click', (evento) => {
        const abrir = evento.target.closest('[data-carrito-abrir]');
        if (abrir) {
          evento.preventDefault();
          this.capa.abrir(abrir);
          return;
        }

        if (evento.target.closest('[data-carrito-cerrar]')) {
          this.capa.cerrar();
        }
      });
    },

    /* Vuelve a pedir el cajón al servidor para que muestre el estado real. */
    async refrescar() {
      const respuesta = await fetch(`${window.location.pathname}?sections=carrito-cajon`);
      if (!respuesta.ok) return;

      const datos = await respuesta.json();
      const html = datos['carrito-cajon'];
      if (!html) return;

      const contenedor = document.querySelector('[data-carrito-cajon]');
      if (!contenedor) return;

      const nuevo = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[data-carrito-cajon]');
      if (!nuevo) return;

      const estabaAbierto = !contenedor.hidden;
      contenedor.innerHTML = nuevo.innerHTML;
      contenedor.hidden = !estabaAbierto;
    },

    async actualizarContador() {
      const respuesta = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      if (!respuesta.ok) return;

      const datos = await respuesta.json();
      /* En corchetes, como el resto de los datos del sistema. Y sin ocultarlo
         en cero: el enlace del carrito en mobile necesita mostrar [0], porque
         si desaparece el contador el enlace queda sin su ancla visual. */
      document.querySelectorAll('[data-carrito-contador]').forEach((nodo) => {
        nodo.textContent = '[' + datos.item_count + ']';
      });
    },
  };

  /* --- Agregar al carrito sin recargar ------------------------------------ */
  function iniciarAgregado() {
    document.addEventListener('submit', async (evento) => {
      const form = evento.target.closest('.producto__form');
      if (!form) return;

      const boton = form.querySelector('button[name="add"]');
      /* El botón de pago rápido debe seguir su camino normal al checkout. */
      if (document.activeElement && document.activeElement.name === 'checkout') return;

      evento.preventDefault();

      const textoOriginal = boton ? boton.textContent : '';
      if (boton) {
        boton.disabled = true;
        boton.textContent = boton.dataset.textoCargando || 'Agregando…';
      }

      try {
        const respuesta = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });

        if (!respuesta.ok) {
          form.submit();
          return;
        }

        const item = await respuesta.json();

        document.dispatchEvent(
          new CustomEvent('brota:carrito:agregado', {
            detail: { producto: item.product_title, variante: item.variant_id },
            bubbles: true,
          })
        );

        await Promise.all([carrito.refrescar(), carrito.actualizarContador()]);

        if (carrito.capa) carrito.capa.abrir(boton);
      } catch (error) {
        /* Si algo falla, se deja que el formulario haga el envío normal. */
        form.submit();
      } finally {
        if (boton) {
          boton.disabled = false;
          boton.textContent = textoOriginal;
        }
      }
    });
  }


  /* --- Scramble de caracteres en hover -------------------------------------
     Los enlaces del sistema técnico resuelven la palabra desde glifos random,
     como en la referencia. Detalles que importan:

     - Solo se tocan los nodos de texto directos, no los hijos: así el contador
       [n] del carrito sobrevive intacto.
     - Los espacios no se scramblean, para que la silueta de la palabra no se
       deforme mientras resuelve.
     - Solo en dispositivos con puntero fino y con hover real. Nada de correrlo
       al enfocar con teclado: cambiar el texto 20 veces por segundo mientras
       un lector de pantalla lo está anunciando es ruido, no efecto.
     - Se apaga entero con prefers-reduced-motion.
     ------------------------------------------------------------------------ */
  const GLIFOS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&/*+-<>[]';
  const DURACION = 340;
  const PROB_TINTA = 0.16;

  function nodosDeTexto(elemento) {
    return Array.from(elemento.childNodes).filter(
      (n) => n.nodeType === Node.TEXT_NODE && n.nodeValue.trim() !== ''
    );
  }

  function scramblear(elemento) {
    if (elemento.dataset.scrambleando === '1') return;
    const nodos = nodosDeTexto(elemento);
    if (!nodos.length) return;

    /* Cada nodo de texto se parte en un span por carácter. Es la única forma
       de teñir letras sueltas: sobre un nodo de texto plano no hay a qué
       aplicarle color. Al terminar se restituye el nodo original, así el DOM
       queda como estaba y el contador [n] del carrito nunca se toca. */
    const tramos = nodos.map((nodo) => {
      const texto = nodo.nodeValue;
      const tramo = document.createElement('span');
      const chars = Array.from(texto).map((ch) => {
        const span = document.createElement('span');
        span.textContent = ch;
        tramo.appendChild(span);
        return span;
      });
      nodo.parentNode.replaceChild(tramo, nodo);
      const desde = texto.search(/\S/);
      const hasta = texto.length - Array.from(texto).reverse().join('').search(/\S/);
      return { texto, tramo, chars, desde, hasta };
    });

    elemento.dataset.scrambleando = '1';
    const inicio = performance.now();

    function paso(ahora) {
      const avance = Math.min(1, (ahora - inicio) / DURACION);

      tramos.forEach(({ texto, chars, desde, hasta }) => {
        const resueltos = desde + Math.floor((hasta - desde) * avance);
        chars.forEach((span, c) => {
          const char = texto[c];
          const fijo = c < resueltos || /\s/.test(char);
          span.textContent = fijo ? char : GLIFOS[Math.floor(Math.random() * GLIFOS.length)];
          /* Solo se tiñen letras que todavía no resolvieron, y pocas: el color
             es un acento del glitch, no un subrayado de todo el texto. */
          span.classList.toggle('glitch-tinta', !fijo && Math.random() < PROB_TINTA);
        });
      });

      if (avance < 1) {
        requestAnimationFrame(paso);
      } else {
        tramos.forEach(({ texto, tramo }) => {
          tramo.parentNode.replaceChild(document.createTextNode(texto), tramo);
        });
        delete elemento.dataset.scrambleando;
      }
    }

    requestAnimationFrame(paso);
  }

  function iniciarScramble() {
    const reducido = window.matchMedia('(prefers-reduced-motion: reduce)');
    const punteroFino = window.matchMedia('(hover: hover) and (pointer: fine)');
    if (reducido.matches || !punteroFino.matches) return;

    document.querySelectorAll('[data-scramble]').forEach((el) => {
      if (el.dataset.scrambleListo === '1') return;
      el.dataset.scrambleListo = '1';
      el.addEventListener('mouseenter', () => scramblear(el));
    });
  }

  /* --- Carrusel de notas ---------------------------------------------------
     El desplazamiento lo hace el scroll nativo con snap; el JS solo empuja
     con prev/next y dibuja las barras indicadoras. Sin JS los controles
     quedan ocultos (.no-js) y el carrusel sigue siendo un scroll común. */
  function iniciarCarruseles() {
    document.querySelectorAll('[data-carrusel]').forEach((vagon) => {
      if (vagon.dataset.carruselListo === '1') return;
      vagon.dataset.carruselListo = '1';

      const lista = vagon.querySelector('[data-carrusel-lista]');
      const prev = vagon.querySelector('[data-carrusel-prev]');
      const next = vagon.querySelector('[data-carrusel-next]');
      const barras = vagon.querySelector('[data-carrusel-barras]');
      if (!lista || !prev || !next) return;

      const items = Array.from(lista.children);

      function paso() {
        return items.length > 0 ? items[0].getBoundingClientRect().width + 1 : lista.clientWidth;
      }

      /* Una barra por "pantalla" de carrusel, no por item. */
      function tramos() {
        return Math.max(1, Math.ceil((lista.scrollWidth - lista.clientWidth) / lista.clientWidth) + 1);
      }

      function pintar() {
        const maximo = lista.scrollWidth - lista.clientWidth;
        prev.disabled = lista.scrollLeft <= 1;
        next.disabled = lista.scrollLeft >= maximo - 1;

        if (!barras) return;
        const total = tramos();
        const activa = maximo > 0 ? Math.round((lista.scrollLeft / maximo) * (total - 1)) : 0;
        if (barras.children.length !== total) {
          barras.textContent = '';
          for (let i = 0; i < total; i += 1) barras.appendChild(document.createElement('i'));
        }
        Array.from(barras.children).forEach((barra, i) => {
          barra.classList.toggle('activa', i === activa);
        });
      }

      prev.addEventListener('click', () => lista.scrollBy({ left: -paso(), behavior: 'smooth' }));
      next.addEventListener('click', () => lista.scrollBy({ left: paso(), behavior: 'smooth' }));
      lista.addEventListener('scroll', pintar, { passive: true });
      window.addEventListener('resize', pintar);
      pintar();
    });
  }

  /* --- Reloj local de "Tu lugar" -------------------------------------------
     Hora del visitante, no del servidor: el dato es "ahora, donde estás".
     El nodo arranca hidden en el markup y solo se muestra si este código
     corre, así sin JS no queda un 00:00:00 congelado mintiendo. */
  let relojTimer = null;

  function iniciarRelojes() {
    const relojes = document.querySelectorAll('[data-reloj]');
    if (!relojes.length) return;

    function pintar() {
      const ahora = new Date();
      const hora = [ahora.getHours(), ahora.getMinutes(), ahora.getSeconds()]
        .map((n) => String(n).padStart(2, '0'))
        .join(':');
      relojes.forEach((reloj) => {
        reloj.hidden = false;
        const nodo = reloj.querySelector('[data-reloj-hora]');
        if (nodo) nodo.textContent = hora;
      });
    }

    pintar();
    /* Al recargar una sección desde el editor, el timer viejo seguía vivo
       apuntando a nodos que ya no están, y el reloj visible quedaba congelado. */
    if (relojTimer) clearInterval(relojTimer);
    relojTimer = setInterval(pintar, 1000);
  }

  /* --- Cortina: transición de salida -------------------------------------
     La entrada la resuelve el CSS solo (ver snippets/cortina.liquid). Acá se
     agrega, si hay JS, que al navegar a otra página del sitio la cortina baje
     antes de irse, así el corte entre páginas no es un flash blanco.

     Es mejora progresiva pura: si algo de esto falla, el link navega igual.
     Por las dudas hay un plazo máximo — si la animación no termina (pestaña
     en segundo plano, por ejemplo), se navega lo mismo. */
  function iniciarCortina() {
    const cortina = document.querySelector('[data-cortina]');
    if (!cortina) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    /* Al volver con el botón atrás la página puede restaurarse desde el
       bfcache con la cortina abajo, tapando todo. Se limpia siempre. */
    window.addEventListener('pageshow', () => {
      cortina.classList.remove('cortina--saliendo');
    });

    document.addEventListener('click', (evento) => {
      if (evento.defaultPrevented) return;
      /* Clic con modificadores o con otro botón: es "abrir en pestaña nueva". */
      if (evento.metaKey || evento.ctrlKey || evento.shiftKey || evento.altKey) return;
      if (evento.button !== 0) return;

      const enlace = evento.target.closest('a[href]');
      if (!enlace) return;
      if (enlace.target && enlace.target !== '_self') return;
      if (enlace.hasAttribute('download')) return;
      if (enlace.dataset.sinCortina !== undefined) return;

      const href = enlace.getAttribute('href');
      if (!href || href.startsWith('#')) return;

      let destino;
      try {
        destino = new URL(enlace.href, window.location.href);
      } catch (error) {
        return;
      }
      /* mailto:, tel:, y cualquier cosa que no sea navegación http(s). */
      if (destino.protocol !== 'http:' && destino.protocol !== 'https:') return;
      /* Sitio externo: se va, no hay transición que valga. */
      if (destino.origin !== window.location.origin) return;
      /* Ancla dentro de la misma página: no es navegación. */
      if (destino.pathname === window.location.pathname && destino.search === window.location.search) return;

      evento.preventDefault();

      let navegado = false;
      const irse = () => {
        if (navegado) return;
        navegado = true;
        window.location.href = destino.href;
      };

      cortina.addEventListener('animationend', irse, { once: true });
      cortina.classList.add('cortina--saliendo');
      setTimeout(irse, 220);
    });
  }

  /* --- Video del hero: se enciende solo si conviene --------------------
     El markup deja el <video> con preload="none" y la fuente en data-src, así
     que por defecto no se descarga NADA. Acá se decide si vale la pena.

     Se abstiene si: la pantalla es angosta (y el merchant no pidió video en
     teléfono), la conexión pide ahorro de datos, la conexión es lenta, o la
     persona pidió menos movimiento. En todos esos casos queda el póster, que
     es una imagen responsive común. */
  function iniciarHeroVideo() {
    const video = document.querySelector('[data-hero-video]');
    if (!video || video.dataset.encendido === '1') return;

    const anchaOk = window.matchMedia('(min-width: 750px)').matches;
    const permitidoEnMovil = video.dataset.enMovil === '1';
    if (!anchaOk && !permitidoEnMovil) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const con = navigator.connection || {};
    if (con.saveData === true) return;
    if (/(^|-)2g$/.test(con.effectiveType || '')) return;

    video.dataset.encendido = '1';
    video.addEventListener('canplay', () => video.classList.add('esta-listo'), { once: true });
    video.src = video.dataset.src;
    video.load();
    /* play() devuelve una promesa que rechaza si el navegador niega el
       autoplay. Sin el catch queda un error sin manejar en consola. */
    const intento = video.play();
    if (intento && intento.catch) intento.catch(() => {});
  }

  function iniciar() {
    iniciarMenu();
    carrito.iniciar();
    iniciarAgregado();
    iniciarScramble();
    iniciarCarruseles();
    iniciarRelojes();
    iniciarCortina();
    iniciarHeroVideo();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', iniciar);
  } else {
    iniciar();
  }

  document.addEventListener('shopify:section:load', iniciar);
})();
